/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

// Cart custom elements that advertise sections via getSectionsToRender().
// If Dawn adds a new cart custom element, add its tag here.
const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

// Walk every mounted Dawn cart custom element, collect the sections it
// wants rendered, and dedupe. Returns a Map keyed by section id, each
// entry pointing at the DOM mount and the selector used to extract the
// fresh fragment.
function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    for (const entry of entries ?? []) {
      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      // Two patterns coexist in getSectionsToRender():
      //   - cart-items style: entry.section is the parent Liquid section
      //     id, entry.selector is a child node inside it.
      //   - cart-drawer / cart-notification style: entry.id IS the mount;
      //     there is no parent wrapper.
      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
        viewElement: el.tagName.toLowerCase() === 'cart-items' && entry.id === 'main-cart-items' ? el : null,
      });
    }
  }

  return sections;
}

// A GET to /cart.js returns cart data only; its sections parameter is ignored.
// Read rendered sections from the Section Rendering API and publish the full
// cart only after the mounted UI has caught up with the completed mutation.
async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const readJson = async (url) => {
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Cart refresh failed: ${response.status}`);
    return response.json();
  };
  const ids = [...sections.keys()];
  const sectionRequests = [];
  // Shopify's Section Rendering API accepts at most five sections per request.
  for (let offset = 0; offset < ids.length; offset += 5) {
    sectionRequests.push(readJson(`${cartUrl}?sections=${encodeURIComponent(ids.slice(offset, offset + 5).join(','))}`));
  }
  const [cartData, renderedBatches] = await Promise.all([
    readJson(`${cartUrl}.js`),
    Promise.all(sectionRequests),
  ]);
  if (!Array.isArray(cartData?.items) || !Number.isInteger(cartData.item_count) || !cartData.currency) {
    throw new Error('Cart refresh returned an incomplete cart');
  }
  const renderedSections = Object.assign({}, ...renderedBatches);
  const replacements = [];
  for (const [id, { mount, extractSelector, viewElement }] of sections) {
    const html = renderedSections[id];
    if (typeof html !== 'string') throw new Error(`Cart section was not rendered: ${id}`);
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const source = parsed.querySelector(extractSelector);
    if (!source) throw new Error(`Cart section is incomplete: ${id}`);
    replacements.push({ mount, source, viewElement, viewPayload: parsed.querySelector('cart-items')?.getAttribute('view-event-payload') });
  }
  for (const { mount, source, viewElement, viewPayload } of replacements) {
    mount.replaceChildren(...source.childNodes);
    if (viewElement && viewPayload) viewElement.setAttribute('view-event-payload', viewPayload);
  }

  document.querySelectorAll('cart-drawer, cart-items, cart-drawer-items, #main-cart-footer').forEach((element) => {
    element.classList.toggle('is-empty', cartData.item_count === 0);
  });
  const drawer = document.querySelector('cart-drawer');
  if (drawer) {
    drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());
    const note = drawer.querySelector('[id^="Details-"] summary');
    if (note && !note.hasAttribute('role')) drawer.setSummaryAccessibility?.(note);
    if (drawer.classList.contains('active') && typeof trapFocus === 'function') {
      const focusRoot = drawer.querySelector('.drawer__inner-empty') || drawer.querySelector('.drawer__inner');
      if (focusRoot) trapFocus(focusRoot, focusRoot.querySelector('button, a') || focusRoot);
    }
  }

  // CartItems skips its duplicate section fetch; shipping estimates, quantity
  // rules, and bulk-add subscribers still receive the authoritative cart.
  await publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'standard-actions',
    sectionsRendered: true,
    cartData: { ...cartData, sections: renderedSections },
  });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    // Dawn doesn't currently listen for shopify:cart:* events, but the
    // bundle requires an eventTarget. document is the conventional root.
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}
